import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTheme } from './context/ThemeContext';
import RequireAuth from './components/auth/RequireAuth';
import RequireRole from './components/auth/RequireRole';
import PublicOnly from './components/auth/PublicOnly';
import LoginPage from './pages/Login/LoginPage';
import LandingPage from './pages/Landing/LandingPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import VideoUploadPage from './pages/Upload/VideoUploadPage';
import VideosPage from './pages/Videos/VideosPage';
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import TeamPage from './pages/Team/TeamPage';
import SettingsPage from './pages/Settings/SettingsPage';
import TenantsPage from './pages/Tenants/TenantsPage';
import PlatformSettingsPage from './pages/PlatformSettings/PlatformSettingsPage';
import VideoDetailPage from './pages/VideoDetail/VideoDetailPage';
import ChangePasswordPage from './pages/Auth/ChangePasswordPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import ProfilePage from './pages/Settings/ProfilePage';
import DealerGroupsPage from './pages/DealerGroups/DealerGroupsPage';
import CoachingPage from './pages/Coaching/CoachingPage';
import GroupAdminDashboardPage from './pages/GroupAdmin/GroupAdminDashboardPage';
import GroupAdminTenantsPage from './pages/GroupAdmin/GroupAdminTenantsPage';
import GroupAdminVideosPage from './pages/GroupAdmin/GroupAdminVideosPage';
import GroupAdminCompliancePage from './pages/GroupAdmin/GroupAdminCompliancePage';
import GroupAdminBenchmarksPage from './pages/GroupAdmin/GroupAdminBenchmarksPage';
import GroupAdminTeamPage from './pages/GroupAdmin/GroupAdminTeamPage';
import GroupAdminTenantDetailPage from './pages/GroupAdmin/GroupAdminTenantDetailPage';
import BenchmarkingPage from './pages/Benchmarking/BenchmarkingPage';
import RoleRedirect from './components/auth/RoleRedirect';
import GlobalUploadIndicator from './components/upload/GlobalUploadIndicator';
import { UserRole } from './types';
import { initAuthSession } from './services/api';

const App: React.FC = () => {
  const [authReady, setAuthReady] = useState(false);
  const { theme } = useTheme();

  const toastColors = theme === 'dark'
    ? {
        bg: 'rgb(42, 42, 42)',
        text: 'rgb(229, 226, 225)',
        border: 'rgb(66, 71, 80)',
        success: 'rgb(158, 202, 255)',
        error: 'rgb(255, 180, 171)',
      }
    : {
        bg: 'rgb(232, 235, 240)',
        text: 'rgb(28, 27, 27)',
        border: 'rgb(194, 199, 209)',
        success: 'rgb(0, 77, 116)',
        error: 'rgb(186, 26, 26)',
      };

  useEffect(() => {
    // Validate/refresh stored tokens on boot before rendering protected routes.
    // This prevents the landing page from showing "Go to Dashboard" when the
    // session has expired (refresh token expired after 8 hours).
    initAuthSession().finally(() => setAuthReady(true));
  }, []);

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="h-6 w-6 rounded-full border-2 border-slate-200 border-t-blue-500 animate-spin" />
      </div>
    );
  }

  return (
  <>
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          background: toastColors.bg,
          color: toastColors.text,
          border: `1px solid ${toastColors.border}`,
          borderRadius: '0px',
          fontFamily: "'Poppins', sans-serif",
          fontSize: '13px',
          letterSpacing: '0.03em',
          padding: '14px 20px',
          maxWidth: '420px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
        success: {
          iconTheme: { primary: toastColors.success, secondary: toastColors.bg },
          style: { borderLeft: `3px solid ${toastColors.success}` },
        },
        error: {
          iconTheme: { primary: toastColors.error, secondary: toastColors.bg },
          style: { borderLeft: `3px solid ${toastColors.error}` },
        },
      }}
    />
    <BrowserRouter>
      <Routes>
        {/* Landing page - accessible to everyone */}
        <Route path="/" element={<LandingPage />} />

        {/* Public routes — redirect to home if already logged in */}
        <Route element={<PublicOnly />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/change-password" element={<ChangePasswordPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Protected routes — redirect to login if not authenticated */}
        <Route element={<RequireAuth />}>
          {/* Routes accessible to all authenticated roles — page-level logic handles role differences */}
          <Route path="/videos/:id" element={<VideoDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />

          {/* Group Admin portal */}
          <Route element={<RequireRole allowedRoles={[UserRole.GROUP_ADMIN]} />}>
            <Route path="/group-admin" element={<GroupAdminDashboardPage />} />
            <Route path="/group-admin/tenants" element={<GroupAdminTenantsPage />} />
            <Route path="/group-admin/videos" element={<GroupAdminVideosPage />} />
            <Route path="/group-admin/compliance" element={<GroupAdminCompliancePage />} />
            <Route path="/group-admin/benchmarks" element={<GroupAdminBenchmarksPage />} />
            <Route path="/group-admin/team" element={<GroupAdminTeamPage />} />
            <Route path="/group-admin/tenants/:tenantId" element={<GroupAdminTenantDetailPage />} />
          </Route>

          {/* Super Admin only — tenant management */}
          <Route element={<RequireRole allowedRoles={[UserRole.SUPER_ADMIN]} />}>
            <Route path="/tenants" element={<TenantsPage />} />
            <Route path="/platform-settings" element={<PlatformSettingsPage />} />
            <Route path="/dealer-groups" element={<DealerGroupsPage />} />
          </Route>

          {/* Tenant users only (TENANT_ADMIN + SALES_REP) — no Super Admin */}
          <Route element={<RequireRole allowedRoles={[UserRole.TENANT_ADMIN, UserRole.SALES_REP]} />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/videos" element={<VideosPage />} />
            <Route path="/coaching" element={<CoachingPage />} />
          </Route>

          {/* Sales Rep only — upload */}
          <Route element={<RequireRole allowedRoles={[UserRole.SALES_REP]} />}>
            <Route path="/upload" element={<VideoUploadPage />} />
          </Route>

          {/* Analytics — both Sales Rep and Tenant Admin */}
          <Route element={<RequireRole allowedRoles={[UserRole.SALES_REP, UserRole.TENANT_ADMIN]} />}>
            <Route path="/analytics" element={<AnalyticsPage />} />
          </Route>

          {/* Benchmarking — TENANT_ADMIN + SUPER_ADMIN */}
          <Route element={<RequireRole allowedRoles={[UserRole.TENANT_ADMIN, UserRole.SUPER_ADMIN]} />}>
            <Route path="/benchmarks" element={<BenchmarkingPage />} />
          </Route>

          {/* Tenant Admin only */}
          <Route element={<RequireRole allowedRoles={[UserRole.TENANT_ADMIN]} />}>
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/team" element={<TeamPage />} />
          </Route>
        </Route>

        {/* Catch-all — redirect based on role */}
        <Route path="*" element={<RoleRedirect />} />
      </Routes>
      <GlobalUploadIndicator />
    </BrowserRouter>
  </>
  );
};

export default App;
